import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import SmsIcon from "@mui/icons-material/Sms";

import {
  DEFAULT_SMS_TEMPLATE,
  MAX_SMS_TEMPLATE_LENGTH,
  SMS_PLACEHOLDERS,
  renderQueueSms,
  smsParts,
} from "./queueFormUtils";

const SAMPLE = {
  name: "Ann",
  number: "#12",
  guests: 4,
  restaurant: "Your restaurant",
  // As long as a real status link, so the SMS count below is right.
  link: "https://orderbuddy.net.au/queue/yourshop/status/AbCdEfGhIjKlMnOpQrSt",
};

// The text guests get when staff call them, from any device (portal, POS or
// MobileBuddy). An empty template means no SMS.
const QueueSmsSettings = ({ savedTemplate, onSave, saving }) => {
  const [enabled, setEnabled] = useState(Boolean(savedTemplate));
  const [template, setTemplate] = useState(savedTemplate || DEFAULT_SMS_TEMPLATE);
  const input = useRef(null);

  useEffect(() => {
    setEnabled(Boolean(savedTemplate));
    setTemplate(savedTemplate || DEFAULT_SMS_TEMPLATE);
  }, [savedTemplate]);

  const trimmed = template.trim();
  const tooLong = trimmed.length > MAX_SMS_TEMPLATE_LENGTH;
  const toSave = enabled ? trimmed : "";
  const valid = !enabled || (trimmed.length > 0 && !tooLong);
  const preview = renderQueueSms(trimmed, SAMPLE);
  const parts = smsParts(preview);

  const insert = (key) => {
    const el = input.current;
    const start = el ? el.selectionStart : template.length;
    const end = el ? el.selectionEnd : template.length;
    setTemplate(template.slice(0, start) + key + template.slice(end));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + key.length, start + key.length);
    });
  };

  return (
    <Card variant="outlined" sx={{ mb: 2, maxWidth: 720 }}>
      <CardContent sx={{ "&:last-child": { pb: 2 } }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <Stack direction="row" spacing={1} alignItems="center">
            <SmsIcon color="action" />
            <Typography variant="h6">SMS when a guest is called</Typography>
          </Stack>
          <FormControlLabel
            control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
            label={enabled ? "On" : "Off"}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: enabled ? 2 : 0 }}>
          Texts the guest&rsquo;s mobile each time staff press Call, on the portal, POS or MobileBuddy. Each
          message is charged as SMS.
        </Typography>

        {enabled ? (
          <>
            <TextField
              inputRef={input}
              label="Message"
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              multiline
              minRows={2}
              fullWidth
              error={tooLong || trimmed.length === 0}
              helperText={
                tooLong
                  ? `At most ${MAX_SMS_TEMPLATE_LENGTH} characters`
                  : trimmed.length === 0
                    ? "Enter a message, or turn SMS off"
                    : `${trimmed.length}/${MAX_SMS_TEMPLATE_LENGTH} characters`
              }
            />
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ my: 1 }}>
              {SMS_PLACEHOLDERS.map(({ key, label }) => (
                <Chip key={key} label={`${label} ${key}`} size="small" variant="outlined" onClick={() => insert(key)} />
              ))}
            </Stack>
            <Box sx={{ bgcolor: "action.hover", borderRadius: 2, p: 1.5, mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Example{parts > 1 ? ` · ${parts} SMS per message` : ""}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {preview}
              </Typography>
            </Box>
          </>
        ) : null}

        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={() => onSave(toSave)}
            disabled={!valid || saving || toSave === (savedTemplate || "")}
          >
            Save
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

QueueSmsSettings.propTypes = {
  savedTemplate: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};

export default QueueSmsSettings;
